import { hasRTSPPublication } from "../utils/streamOutputs";
import RTSPLinks from "./RTSPLinks";
import SeiStatus from "./SeiStatus";
import WhipStatus from "./WhipStatus";
import LlHlsLinks from "./LlHlsLinks";
import type { BitrateChartData } from "../utils/bitrateHistory";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Toolbar,
  Tooltip,
  Typography
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { streamCardFonts, streamCardSurfaceColors } from "../theme";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SettingsIcon from "@mui/icons-material/Settings";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import ConnectedTvIcon from "@mui/icons-material/ConnectedTv";
import PreviewIcon from "@mui/icons-material/Preview";
import ViewTimelineIcon from "@mui/icons-material/ViewTimeline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FiberSmartRecordIcon from "@mui/icons-material/FiberSmartRecord";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import StreamState, { type StreamStateValue } from "./StreamState";
import SreamStateSpinner from "./SreamStateSpinner";
import StreamBitrateChartTiny from "./StreamBitrateChartTiny";
import StreamPreviewTile from "./StreamPreviewTile";
import StreamPreviewPopover, { type StreamPreviewPopoverOpenAnimation } from "./StreamPreviewPopover";
import type { StreamSummary } from "../types/streamSummary";
import type { FileSourceInfoSnapshot } from "../types/fileSourceInfo";
import { useAppContext } from "../context/AppContext";
import {
  isStreamRunningState,
  isStreamConfigReadOnly,
  isStreamStartingState,
  hasStoppedMessage,
  shouldShowStreamActivitySpinner
} from "../utils/streamState";
import {
  extractRecastOutputs,
  resolveOutputState,
  type StreamOutputStatsSnapshot,
  isRecastMode,
  isRecastOnlyMode
} from "../utils/streamOutputs";

type StreamCardProps = {
  stream: StreamSummary;
  canManageStreams: boolean;
  canViewDetectionInfo: boolean;
  pendingCommand?: "start" | "stop";
  isTimelineSelected: boolean;
  isCalendarSelected: boolean;
  bitrates: BitrateChartData;
  previewUrl?: string;
  fileSourceInfo?: FileSourceInfoSnapshot;
  streamOutputStats?: Map<string, StreamOutputStatsSnapshot>;
  onCommand: (args: { streamId: string; command: "start" | "stop" }) => void;
  onOpenEdit: (stream: StreamSummary) => void;
  onOpenDelete: (stream: StreamSummary) => void;
  onOpenDetection: (stream: StreamSummary) => void;
  onToggleTimeline: (stream: StreamSummary) => void;
  onToggleCalendar: (stream: StreamSummary) => void;
  onOpenPlayer: (stream: StreamSummary) => void;
  getStreamState: (streamId: string) => StreamStateValue | undefined;
  getStreamStateMessage: (streamId: string) => unknown;
  getOutputState: (streamId: string, outputId: string) => StreamStateValue | undefined;
  getOutputStateMessage: (streamId: string, outputId: string) => string | null;
  hideStreamName?: boolean;
  previewOpenAnimation?: StreamPreviewPopoverOpenAnimation;
};

const StreamCard = ({
  stream,
  canManageStreams,
  canViewDetectionInfo,
  pendingCommand,
  isTimelineSelected,
  isCalendarSelected,
  bitrates,
  previewUrl,
  fileSourceInfo,
  streamOutputStats,
  onCommand,
  onOpenEdit,
  onOpenDelete,
  onOpenDetection,
  onToggleTimeline,
  onToggleCalendar,
  onOpenPlayer,
  getStreamState,
  getStreamStateMessage,
  getOutputState,
  getOutputStateMessage,
  hideStreamName = false,
  previewOpenAnimation = "none"
}: StreamCardProps) => {
  const { serverConfig } = useAppContext();
  const navigate = useNavigate();
  const { authorizedFetch } = useAuth();
  const recordingsQuery = useQuery({
    queryKey: ["recordings", "stream-status"],
    enabled: canManageStreams && serverConfig.recastLicensed && serverConfig.recordLicensed,
    refetchInterval: 2000,
    queryFn: async () => {
      const response = await authorizedFetch("/api/recordings");
      if (!response.ok) return [] as Array<{ id: string; name: string; status: string; files: Array<{ streamIds: string[] }>; stats?: { streams?: Record<string, { bitrate?: number }> } }>;
      return response.json() as Promise<Array<{ id: string; name: string; status: string; files: Array<{ streamIds: string[] }>; stats?: { streams?: Record<string, { bitrate?: number }> } }>>;
    }
  });
  const linkedRecording = recordingsQuery.data?.find((job) => job.files.some((group) => group.streamIds.includes(stream.id)));
  const recordingBitrate = linkedRecording?.stats?.streams?.[stream.id]?.bitrate;
  const streamState = getStreamState(stream.id);
  const streamStateMessage = getStreamStateMessage(stream.id);
  const streamHasError = streamState === "error";
  const isStreamStarting = isStreamStartingState(streamState, streamStateMessage);
  const trimmedErrorMessage = streamHasError && typeof streamStateMessage === "string"
    ? streamStateMessage.trim()
    : "";
  const errorCaption = streamHasError && trimmedErrorMessage ? `Error: ${trimmedErrorMessage}` : null;
  const streamStateTooltip = errorCaption ?? undefined;
  const isStreamRunning = isStreamRunningState(streamState, streamStateMessage) || hasRTSPPublication(streamOutputStats);
  const configReadOnly = isStreamConfigReadOnly(streamState, streamStateMessage, pendingCommand);
  const isCommandPending = pendingCommand === "start" ? !isStreamRunning : pendingCommand === "stop" && isStreamRunning;
  const showSpinner = shouldShowStreamActivitySpinner(streamState, streamStateMessage);
  const controlDisabled = isCommandPending || isStreamStarting || (!stream.active && !isStreamRunning);
  const controlTooltip = isCommandPending
    ? "Sending command..."
    : isStreamStarting
      ? "Starting"
    : isStreamRunning
      ? streamHasError
        ? "Stop stream to clear error"
        : "Stop stream"
      : !stream.active
        ? "Stream must be active to control"
        : "Start stream";
  const isDataPayload = (stream.payloadType ?? "").trim().toLowerCase() === "data";
  const isRecastOnly = isRecastOnlyMode(stream.mode);
  const hasRecastMode = isRecastMode(stream.mode);
  const recastOutputs = hasRecastMode ? extractRecastOutputs(stream.targets) : [];
  const showInlinePreview = serverConfig.enablePreview && !isDataPayload;
  const previewTitle = stream.platformName ? `${stream.platformName} / ${stream.sensor}` : stream.sensor;
  const isStopped = hasStoppedMessage(streamStateMessage);
  const stateLabel = isStreamStarting ? "Starting" : isStopped ? "Stopped" : {
    online: "Receiving",
    offline: "No signal",
    timeout: "Timeout",
    connecting: "Connecting",
    unknown: "Unknown",
    error: "Error"
  }[streamState ?? "unknown"];
  const stateColor = isStopped ? "text.secondary" : streamHasError ? "error.main"
    : streamState === "online" ? "success.main"
    : isStreamStarting || streamState === "connecting" || streamState === "timeout" ? "warning.main"
    : "text.secondary";

  return (
    <Card
      variant="outlined"
      sx={{
        display: "flex", flexDirection: "column", flexGrow: 1, minWidth: 0, width: "100%",
        borderRadius: 1.5, backgroundImage: "none",
        bgcolor: (theme) => streamCardSurfaceColors[theme.palette.mode].paper,
        color: (theme) => streamCardSurfaceColors[theme.palette.mode].foreground,
        borderColor: (theme) => streamCardSurfaceColors[theme.palette.mode].border
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 0, bgcolor: "inherit", "&:last-child": { pb: 1 } }}>
        <Box sx={{ px: 2, pt: 1.25, pb: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 0.75 }}>
            <Chip
              label={stream.type.toUpperCase()}
              size="small"
              sx={{
                height: 22, borderRadius: 1, fontFamily: streamCardFonts.address, fontSize: "0.7rem",
                letterSpacing: "0.04em", color: (theme) => theme.palette.mode === "dark" ? theme.palette.primary.main : theme.palette.primary.dark,
                bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.1)
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
              <Typography variant="caption" color="text.secondary">{stream.active ? "Enabled" : "Disabled"}</Typography>
              <StreamState state={isStopped ? "offline" : streamState} size={8} tooltip={streamStateTooltip} />
              <Typography variant="caption" color={stateColor}>{stateLabel}</Typography>
              <SreamStateSpinner
                streamHasError={streamHasError}
                showSpinner={showSpinner}
                errorCaption={errorCaption}
                progress={fileSourceInfo?.progress}
                position={fileSourceInfo?.position}
                loopCount={fileSourceInfo?.loopCount}
              />
            </Box>
          </Box>
          <Box sx={{ minWidth: 0, mt: hideStreamName ? 0 : 0.75 }}>
            {!hideStreamName ? (
              <Typography variant="subtitle1" sx={{ fontFamily: streamCardFonts.name, fontWeight: 600, lineHeight: 1.35, letterSpacing: "-0.015em", overflowWrap: "anywhere" }}>{stream.sensor}</Typography>
            ) : null}
            <Typography component="div" variant="caption" color="text.secondary" aria-hidden={!stream.platformName} sx={{ visibility: stream.platformName ? "visible" : "hidden" }}>
              {stream.platformName || "\u00a0"}
            </Typography>
          </Box>
        </Box>
        {stream.description ? <Typography variant="body2" color="text.secondary" sx={{ px: 2, mb: 0.75, overflowWrap: "anywhere" }}>{stream.description}</Typography> : null}
        <Box sx={{ px: 2, pb: 1 }}>
          <Tooltip
            title={stream.url}
            describeChild
            slotProps={{ tooltip: { sx: { maxWidth: "min(360px, calc(100vw - 32px))", maxHeight: "min(480px, calc(100vh - 32px))", overflowY: "auto", overflowWrap: "anywhere" } } }}
          >
            <Typography component="div" variant="caption" noWrap tabIndex={0} sx={{ fontFamily: streamCardFonts.address, color: (theme) => streamCardSurfaceColors[theme.palette.mode].muted }}>
              {stream.url}
            </Typography>
          </Tooltip>
        </Box>
        {linkedRecording ? <Box sx={{ px: 2, pb: 0.75 }}><Chip size="small" color={linkedRecording.status === "running" ? "error" : "default"} sx={{ height: "auto", "& .MuiChip-label": { py: 0.25, whiteSpace: "normal", overflowWrap: "anywhere" } }} label={`${linkedRecording.name}: ${linkedRecording.status}${typeof recordingBitrate === "number" ? ` · ${recordingBitrate.toFixed(2)} Mbit/s` : ""}`} /></Box> : null}
        {errorCaption ? (
          <Typography
            variant="caption"
            color="error.main"
            sx={{ display: "block", px: 2, pb: 0.75, overflowWrap: "anywhere" }}
          >
            {errorCaption}
          </Typography>
        ) : null}
        <Box
          sx={{
            px: 2, py: 0.5, display: "grid", gap: 1.75, alignItems: "center",
            gridTemplateColumns: showInlinePreview ? "minmax(0, min(42%, 180px)) minmax(0, 1fr)" : "minmax(0, 1fr)",
            bgcolor: (theme) => streamCardSurfaceColors[theme.palette.mode].monitor,
            borderBlock: "1px solid", borderColor: (theme) => streamCardSurfaceColors[theme.palette.mode].border
          }}
        >
          {showInlinePreview ? (
            <StreamPreviewPopover
              streamId={stream.id}
              title={previewTitle}
              streamState={streamState}
              openAnimation={previewOpenAnimation}
            >
              <Box
                sx={{
                  width: "100%",
                  aspectRatio: "16 / 9",
                  flexShrink: 0,
                  borderRadius: 1,
                  boxShadow: (theme) => `0 0 0 1px ${theme.palette.divider}`,
                  overflow: "hidden"
                }}
              >
                <StreamPreviewTile
                  title={previewTitle}
                  previewUrl={previewUrl}
                  streamState={streamState}
                  streamStateMessage={streamStateMessage}
                  compact
                  height="100%"
                />
              </Box>
            </StreamPreviewPopover>
          ) : null}
          <Box sx={{ minWidth: 0 }}>
            <StreamBitrateChartTiny bitrates={bitrates} height={78} showGrid showValue />
          </Box>
        </Box>
        {recastOutputs.length ? (
          <Box sx={{ px: 2, py: 1, display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Outputs · {recastOutputs.length}
            </Typography>
            {recastOutputs.map((output) => {
              const outputStats = streamOutputStats?.get(output.id);
              const hasBytesSent = outputStats?.whip?.bytesAvailable !== false && typeof outputStats?.bytesSent === "number";
              const rawOutputState = getOutputState(stream.id, output.id);
              const outputState = resolveOutputState(rawOutputState, outputStats, isStreamRunning, output.enabled);
              const hasBitrate = outputState === "online" &&
                typeof outputStats?.bitrate === "number" &&
                Number.isFinite(outputStats.bitrate) &&
                outputStats.bitrate > 0;
              const bytesSentTooltip = hasBytesSent
                ? `${(outputStats!.bytesSent / (1024 * 1024)).toFixed(2)} MB ${output.type === "rtsp" ? <RTSPLinks stats={outputStats} fallback={output.url} /> : output.type === "whip" ? <WhipStatus stats={outputStats} /> : output.type === "llhls" ? "published" : "sent"}`
                : "";
              const bitrateLabel = hasBitrate ? `${outputStats!.bitrate.toFixed(2)} Mbit/s` : "";
              const isAddressOutput = output.type !== "whip" && output.type !== "llhls";
              const outputError = outputState === "error"
                ? getOutputStateMessage(stream.id, output.id)
                : null;

              return (
                <Box
                  key={output.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "12px minmax(0, 1fr)",
                    alignItems: "baseline",
                    columnGap: 0.75,
                    rowGap: 0.25
                  }}
                >
                  <StreamState
                    state={outputState}
                    size={12}
                    tooltip={outputError ? `Error: ${outputError}` : undefined}
                  />
                  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", justifyContent: "space-between", gap: 0.5, minWidth: 0 }}>
                    <Tooltip
                      title={output.description ?? ""}
                      disableHoverListener={!output.description}
                    >
                      <Typography variant="body2" sx={{ fontFamily: streamCardFonts.name, fontWeight: 600, minWidth: 0, overflowWrap: "anywhere" }}>
                        {output.name}
                      </Typography>
                    </Tooltip>
                    {bitrateLabel ? (
                      <Tooltip title={bytesSentTooltip} disableHoverListener={!hasBytesSent}>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{bitrateLabel}</Typography>
                      </Tooltip>
                    ) : null}
                  </Box>
                  <Tooltip
                    title={isAddressOutput ? [output.url, bytesSentTooltip].filter(Boolean).join("\n") : bytesSentTooltip}
                    disableHoverListener={!isAddressOutput && !hasBytesSent}
                    describeChild
                    slotProps={{ tooltip: { sx: { maxWidth: "min(360px, calc(100vw - 32px))", maxHeight: "min(480px, calc(100vh - 32px))", overflowY: "auto", overflowWrap: "anywhere", whiteSpace: "pre-line" } } }}
                  >
                    <Box tabIndex={isAddressOutput ? 0 : undefined} sx={{ gridColumn: 2, minWidth: 0 }}>
                      <Typography component="div" variant="caption" noWrap={isAddressOutput} sx={{ fontFamily: streamCardFonts.address, overflowWrap: "anywhere", color: (theme) => streamCardSurfaceColors[theme.palette.mode].muted }}>
                        <SeiStatus stats={outputStats} running={isStreamRunning && output.enabled !== false} />
                        {output.type === "rtsp" ? <RTSPLinks stats={outputStats} fallback={output.url} /> : output.type === "whip" ? <WhipStatus stats={outputStats} /> : output.type === "llhls" ? <LlHlsLinks stats={outputStats} fallback={output.url} /> : output.url}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        ) : null}
      </CardContent>
      {(isStreamRunning || streamHasError || canManageStreams) ? (
        <CardActions sx={{ mt: "auto", px: 1.25, py: 0.5, bgcolor: "inherit", borderTop: "1px solid", borderColor: (theme) => streamCardSurfaceColors[theme.palette.mode].border }}>
          <Toolbar
            disableGutters
            variant="dense"
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.25,
              backgroundColor: "inherit",
              color: "inherit",
              minHeight: "36px !important",
              py: 0,
              px: 0,
              "& .MuiIconButton-root": {
                color: "text.secondary",
                borderRadius: 1,
                "&.MuiIconButton-colorPrimary": { color: "primary.main" },
                "&.Mui-disabled": { color: "action.disabled" },
                "@media (pointer: coarse)": { minWidth: 44, minHeight: 44 }
              }
            }}
          >
            {isStreamRunning ? (
              <>
                {!isDataPayload && !isRecastOnly ? (
                  <StreamPreviewPopover
                    streamId={stream.id}
                    title={previewTitle}
                    streamState={streamState}
                    disabled={!serverConfig.enablePreview}
                    openAnimation={previewOpenAnimation}
                  >
                    <IconButton size="small" aria-label="Open stream player" onClick={() => onOpenPlayer(stream)}>
                      <ConnectedTvIcon fontSize="small" />
                    </IconButton>
                  </StreamPreviewPopover>
                ) : null}
                {!isDataPayload && isRecastOnly && serverConfig.enablePreview ? (
                  <StreamPreviewPopover
                    streamId={stream.id}
                    title={previewTitle}
                    streamState={streamState}
                    openAnimation={previewOpenAnimation}
                  >
                    <IconButton size="small" aria-label="Stream preview">
                      <PreviewIcon fontSize="small" />
                    </IconButton>
                  </StreamPreviewPopover>
                ) : null}
                {!isDataPayload && canViewDetectionInfo ? (
                  <>
                    <Tooltip title="Stream detection info">
                      <IconButton size="small" aria-label="Stream detection info" onClick={() => onOpenDetection(stream)}>
                        <TroubleshootIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : null}
                <Tooltip title="Stream events timeline">
                  <IconButton
                    size="small"
                    aria-label="Stream events timeline"
                    color={isTimelineSelected ? "primary" : "default"}
                    aria-pressed={isTimelineSelected}
                    onClick={() => onToggleTimeline(stream)}
                  >
                    <ViewTimelineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : null}
            <Tooltip title="Stream calendar">
              <IconButton
                size="small"
                aria-label="Stream calendar"
                color={isCalendarSelected ? "primary" : "default"}
                aria-pressed={isCalendarSelected}
                onClick={() => onToggleCalendar(stream)}
              >
                <CalendarMonthIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canManageStreams ? (
              <>
                <Tooltip title={configReadOnly ? "Show Config" : "Edit stream"}>
                  <IconButton size="small" aria-label={configReadOnly ? "Show Config" : "Edit stream"} onClick={() => onOpenEdit(stream)}>
                    {configReadOnly ? <SettingsIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                {serverConfig.recastLicensed && serverConfig.recordLicensed && stream.active && stream.type.toLowerCase() === "udp" ? (
                  <Tooltip title="Add to recording">
                    <IconButton size="small" aria-label="Add to recording" onClick={() => navigate(`/recordings?stream=${encodeURIComponent(stream.id)}`)}><FiberSmartRecordIcon fontSize="small" /></IconButton>
                  </Tooltip>
                ) : null}
                <Tooltip title="Delete stream">
                  <IconButton size="small" aria-label="Delete stream" onClick={() => onOpenDelete(stream)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title={controlTooltip}>
                  <Box component="span" sx={{ ml: "auto" }}>
                    <IconButton
                      size="small"
                      aria-label={isStreamRunning ? "Stop stream" : "Start stream"}
                      disabled={controlDisabled}
                      onClick={() =>
                        onCommand({
                          streamId: stream.id,
                          command: isStreamRunning ? "stop" : "start"
                        })
                      }
                    >
                      {isCommandPending || isStreamStarting ? (
                        <CircularProgress size={16} thickness={5} />
                      ) : isStreamRunning ? (
                        <StopCircleIcon fontSize="small" />
                      ) : (
                        <PlayCircleIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                </Tooltip>
              </>
            ) : (
              <Box sx={{ flexGrow: 1 }} />
            )}
          </Toolbar>
        </CardActions>
      ) : null}
    </Card>
  );
};

export default StreamCard;
